import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
  navigatorContractorExpansionStore,
  type NavigatorExpansionStore,
  navigatorGroupExpansionStore,
} from './navigatorExpansionSessionCache';

const EMPTY_IDS: ReadonlySet<string> = new Set();

const filterToValidIds = (ids: ReadonlySet<string>, validIds: readonly string[]): ReadonlySet<string> => {
  if (ids.size === 0) return EMPTY_IDS;
  const valid = new Set(validIds);
  const filtered = new Set([...ids].filter(id => valid.has(id)));
  if (filtered.size === ids.size) return ids;
  return filtered.size ? filtered : EMPTY_IDS;
};

const readCachedExpandedIds = (
  projectId: string | undefined,
  store: NavigatorExpansionStore,
  validIds: readonly string[],
): ReadonlySet<string> => {
  if (!projectId) return EMPTY_IDS;
  const cached = store.read(projectId);
  if (!cached?.size) return EMPTY_IDS;
  return filterToValidIds(cached, validIds);
};

/**
 * Reconcile local expansion with the scan's current group/contractor ids.
 *
 * - No valid ids yet (scan not loaded, or a genuinely empty scan) → hold what we
 *   have. Pruning against an empty universe can't tell "not here yet" from
 *   "gone", and would collapse expansion on a transient.
 * - Non-empty prune → keep the filtered set.
 * - Prune to empty while local state still had ids → those ids are stale (e.g. a
 *   scan fork). Collapse them from render, but never write empty: an empty write
 *   deletes the project's cache entry outright (see navigatorExpansionSessionCache),
 *   and clearing is reserved for an explicit user collapse. Leaving the cache
 *   intact lets a later remount/restore recover it.
 * - Local state already empty → restore from session cache so a remount or late-
 *   arriving group list can pick expansion back up without a collapse flash.
 */
const reconcileExpandedIds = (
  projectId: string | undefined,
  previous: ReadonlySet<string>,
  validIds: readonly string[],
  store: NavigatorExpansionStore,
  restoreFromCache: boolean,
): ReadonlySet<string> => {
  if (validIds.length === 0) return previous;
  const pruned = filterToValidIds(previous, validIds);
  if (restoreFromCache) {
    /* First reconcile with a real universe for this project: merge saved
       expansion back in even if local state was already seeded (the selection
       nudge may have added `{currentGroupId}` while projectId was still
       undefined, so the lazy initializer restored nothing). */
    const cached = readCachedExpandedIds(projectId, store, validIds);
    if (cached.size > 0) {
      const merged = new Set(pruned);
      cached.forEach(id => merged.add(id));
      const changed = merged.size !== previous.size || [...merged].some(id => !previous.has(id));
      if (changed) {
        store.write(projectId, merged);
        return merged;
      }
    }
  }
  if (pruned.size > 0) {
    if (pruned === previous) return previous;
    store.write(projectId, pruned);
    return pruned;
  }
  if (previous.size > 0) return EMPTY_IDS;
  const restored = readCachedExpandedIds(projectId, store, validIds);
  if (restored.size === 0 || restored === previous) return previous;
  store.write(projectId, restored);
  return restored;
};

const useExpandedIdsSlot = (
  projectId: string | undefined,
  validIds: readonly string[],
  store: NavigatorExpansionStore,
) => {
  const [expandedIds, setExpandedIdsInner] = useState<ReadonlySet<string>>(
    () => readCachedExpandedIds(projectId, store, validIds),
  );

  /**
   * A write inside the updater fires even when React bails out on an unchanged
   * result — the selection nudge returns `previous` when the group is already
   * open — and double-fires under StrictMode, which lets a no-op clobber a rich
   * cached set with sparse local state. Stash the intended value instead and let
   * an effect persist it after the state settles. Only a genuine change (the
   * updater returned a new set) schedules a write, so a bail-out writes nothing.
   */
  const pendingWriteRef = useRef<ReadonlySet<string> | null>(null);
  const setExpandedIds = useCallback((
    updater: ReadonlySet<string> | ((previous: ReadonlySet<string>) => ReadonlySet<string>),
  ) => {
    setExpandedIdsInner(previous => {
      const next = typeof updater === 'function' ? updater(previous) : updater;
      pendingWriteRef.current = next === previous ? null : next;
      return next;
    });
  }, []);

  useEffect(() => {
    if (pendingWriteRef.current === null) return;
    store.write(projectId, pendingWriteRef.current);
    pendingWriteRef.current = null;
  }, [expandedIds, projectId, store]);

  /* Restore from the session cache once per project, the first time a real
     universe of valid ids is available — whether projectId or the group/
     contractor list is the thing that arrives late. The lazy useState
     initializer is otherwise the only restore point, and it runs too early when
     projectId is still undefined or the list hasn't loaded. */
  const restoredForProjectRef = useRef<string | undefined>(undefined);
  useLayoutEffect(() => {
    const restoreFromCache = !!projectId && validIds.length > 0 && restoredForProjectRef.current !== projectId;
    if (restoreFromCache) restoredForProjectRef.current = projectId;
    setExpandedIdsInner(previous => reconcileExpandedIds(projectId, previous, validIds, store, restoreFromCache));
  }, [projectId, validIds, store]);

  return { expandedIds, setExpandedIds };
};

export const useNavigatorExpansionState = (
  projectId: string | undefined,
  validGroupIds: readonly string[],
  validContractorIds: readonly string[],
) => {
  const { expandedIds: expandedGroupIds, setExpandedIds: setExpandedGroupIds } = useExpandedIdsSlot(
    projectId,
    validGroupIds,
    navigatorGroupExpansionStore,
  );
  const { expandedIds: expandedContractorIds, setExpandedIds: setExpandedContractorIds } = useExpandedIdsSlot(
    projectId,
    validContractorIds,
    navigatorContractorExpansionStore,
  );

  return {
    expandedGroupIds,
    expandedContractorIds,
    setExpandedGroupIds,
    setExpandedContractorIds,
  };
};
